import {Component, Inject, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {BehaviorSubject, firstValueFrom, from, Subscription} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TreeNode, TreeOptions, TreeService} from '@mydocma/component-library/src/lib/tree';
import {Pool} from '@docma-mm-core/pool/pool';
import {Fault} from '@docma-mm-core/fault/fault';
import {EntityScopeEnum} from '@docma-mm-core/entity/entity-scope.enum';
import {MmProjectDownloadService} from '../../../mm-project/mm-download.service';
import {MmViewService} from '../../../mm-view.service';
import {map, switchMap} from 'rxjs/operators';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {SettingNamesEnum} from '../../../mydocma-settings/setting-names.enum';
import {GetValue, SettingsService} from '../../../mydocma-settings/settings.service';
import {ProjectService} from '@mydocma/project';
import {RecentlyUsedPoolsService} from '../services/recently-used-pools.service';
import {JwtAuthService} from '@mydocma/auth';
import {MmFilterService} from '../../mm-filter.service';
import { Logger } from '@mydocma/common/src/lib/logger';
import { PromptDialogService } from '@mydocma/component-library/src/lib/prompt-dialog';
import { ConnectivityService } from '@mydocma/core';

export interface IPoolListToggleDialogData {
  faults?: Fault []
  fault?: Fault;
  isAdd: boolean;
}

interface PoolScope {
  scope: number;
  text?: string;
  icon?: string;
  pools?: Pool[];
}

@UntilDestroy()
@Component({
  selector: 'mydocma-pool-list-toggle',
  templateUrl: './pool-list-toggle.component.html',
  styleUrls: ['./pool-list-toggle.component.scss']
})
export class PoolListToggleComponent implements OnInit {

  public poolTreeOptions: TreeOptions;
  private treeService: TreeService;

  public loading = new BehaviorSubject<boolean>(false);

  entityScopes = EntityScopeEnum;
  recentlyUsedScopeId = 99;
  poolsLoaded: Pool[];
  poolScopesLoaded: PoolScope[] = [];
  recentlyUsedPools: Pool[] = [];

  private nodeExpandedStates: {[id: string]: {node: TreeNode, isExpanded: boolean}} = {};
  private subscriptions: Subscription[] = [];

  scopeSortSetting: { [scope: string]: number };
  poolScopesSubject = new BehaviorSubject<PoolScope[]>([]);
  poolScopes$ = this.poolScopesSubject.asObservable();

  selectedPools = new Set<number>();
  selectedPoolsSubject = new BehaviorSubject<Set<number>>(new Set());
  selectedPools$ = this.selectedPoolsSubject.asObservable();

  titleSubject = new BehaviorSubject<string>('');
  title$ = this.titleSubject.asObservable();

  isDoneDisabled = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: IPoolListToggleDialogData,
    private dialogRef: MatDialogRef<PoolListToggleComponent>,
    private translateService: TranslateService,
    private readonly mmViewService: MmViewService,
    private readonly downloadService: MmProjectDownloadService,
    private readonly settingsService: SettingsService,
    private readonly projectService: ProjectService,
    private readonly recentlyUsedPoolsService: RecentlyUsedPoolsService,
    private readonly jwtAuthService: JwtAuthService,
    private readonly mmFilterService: MmFilterService,
    private readonly logger: Logger,
    private connectivityService: ConnectivityService,
    private dialogService: PromptDialogService
  ) {
    this.setupTitle();
    this.setupTreeOptions();


    this.selectedPools$.pipe(untilDestroyed(this)).subscribe(selectedPools => {
      this.isDoneDisabled = selectedPools.size === 0;
    });
  }

  ngOnInit(): void {
    this.loadPools();
  }

  private setupTitle(): void {
    const defectCount = this.dialogData.faults ? this.dialogData.faults.length : 1;
    const isSingle = defectCount === 1;
    
    let titleKey: string;
    if (this.dialogData.isAdd) {
      titleKey = isSingle ? 'MM.pool.listToggle.addTitleSingle' : 'MM.pool.listToggle.addTitlePlural';
    } else {
      titleKey = isSingle ? 'MM.pool.listToggle.removeTitleSingle' : 'MM.pool.listToggle.removeTitlePlural';
    }

    const title = this.translateService.instant(titleKey, { count: defectCount });
    this.titleSubject.next(title);
  }

  private setupTreeOptions(): void {
    this.poolTreeOptions = {
      suppressChildrenCount: true,
      toolbar: {
        buttons: {
          expandCollapseAll: {hidden: true},
        }
      },
      getNodeId: (data: any) => {
        if (data.scope) {
          return 'scope-' + data.scope.toString();
        } else {
          return data.Id.toString();
        }
      },
      getNodeInfo: (node: TreeNode) => {
        if (node.data.scope) {
          const isExpanded = this.getNodeExpandedState(node);
          return {
            text: node.data.text,
            icon: node.data.icon,
            expanded: isExpanded,
            selectable: false
          };
        }
        return {
          text: node.data.Name,
          selectable: false
        };
      },
      useFilterCallbackForEmptyFilter: true,
      isNodeInFilter: (node: TreeNode, filters: string[]) => {
        const filterStr = ((this.treeService.filterService.filters?.length ?? 0) > 0 ? this.treeService.filterService.filters[0].toLowerCase() : null);

        if (node.data.scope) {
          const hasVisiblePools = (node.data.pools.length > 0 && node.data.pools.some(l => !(filterStr != null && l.Name.toLowerCase().indexOf(filterStr) === -1) ));
          if (!hasVisiblePools) {
            return false;
          }
        }
        else {
          if ( (filterStr != null && node.data.Name.toLowerCase().indexOf(filterStr) === -1) ) {
            return false;
          }
        }
        return null;
      },
      getNodeClass: (node: TreeNode) => {
        if (node.data.scope) {
          return 'pool-scope-node';
        }
        return 'pool-node';
      },
      getNodeChildren: (node: TreeNode) => {
        if (node.data.scope) {
          return node.data.pools;
        }
        return [];
      },
      onReady: async (ts: TreeService) => {
        this.treeService = ts;

        this.subscriptions.push(this.treeService.onNodeExpanded.pipe(untilDestroyed(this)).subscribe(async n => {
          const hasFilter = await firstValueFrom(this.treeService.filterService.filters$.pipe(map(f => (f?.length ?? 0) > 0 && f[0] != '')) );
          if (!hasFilter && n) {
            if (this.nodeExpandedStates.hasOwnProperty(n.id)) {
              this.nodeExpandedStates[n.id].isExpanded = true;
            }
            else {
              this.nodeExpandedStates[n.id] = {node: n, isExpanded: true};
            }
          }
        }));

        this.subscriptions.push(this.treeService.onNodeCollapsed.pipe(untilDestroyed(this)).subscribe(async n => {
          const hasFilter = await firstValueFrom(this.treeService.filterService.filters$.pipe(map(f => (f?.length ?? 0) > 0 && f[0] != '')));
          if (!hasFilter && n) {
            if (this.nodeExpandedStates.hasOwnProperty(n.id)) {
              this.nodeExpandedStates[n.id].isExpanded = false;
            }
            else {
              this.nodeExpandedStates[n.id] = {node: n, isExpanded: false};
            }
          }
        }));
      }
    };
  }

  private async loadPools(): Promise<void> {
    this.loading.next(true);

    try {
      const currentProject = await firstValueFrom(this.mmViewService.currentProject$);
      if (!currentProject) {
        this.loading.next(false);
        return;
      }

      const pools = await firstValueFrom(this.downloadService.downloadPools(currentProject));
      const sortSetting = await this.loadScopeSortSetting();

      this.scopeSortSetting = sortSetting;
      this.poolsLoaded = pools;

      const schemaId = this.jwtAuthService.getAccessTokenClaims().db_id;
      const recentlyUsedIds = await this.recentlyUsedPoolsService.getRecentlyUsedPoolIds(schemaId);
      if (recentlyUsedIds?.length > 0) {
        const recentlyUsedIdSet = new Set(recentlyUsedIds);
        const recentPools = this.poolsLoaded.filter(p => recentlyUsedIdSet.has(p.Id));
        this.recentlyUsedPools = recentPools.sort((a, b) => recentlyUsedIds.indexOf(b.Id) - recentlyUsedIds.indexOf(a.Id));
      }

      this.getPoolScopes();
      this.poolScopesSubject.next(this.poolScopesLoaded);

      await this.preselectCurrentlyFilteredPools();

    } catch (error) {
      this.logger.error(error, '', 'PoolListToggleComponent');
    } finally {
      this.loading.next(false);
    }
  }

  private async preselectCurrentlyFilteredPools(): Promise<void> {
    try {
      const filterState = await firstValueFrom(this.mmFilterService.state);
      
      if (filterState?.poolInclusionFilters?.length > 0) {
        const currentlyFilteredPoolIds = filterState.poolInclusionFilters.map(pool => pool.Id);
        
        for (const poolId of currentlyFilteredPoolIds) {
          if (this.poolsLoaded.some(p => p.Id === poolId)) {
            this.selectedPools.add(poolId);
          }
        }
        
        this.selectedPoolsSubject.next(new Set(this.selectedPools));
      }
    } catch (error) {
      this.logger.error(error, '', 'PoolListToggleComponent');
    }
  }

  private async loadScopeSortSetting(): Promise<{ [scope: string]: number }> {
    const defaultSortSetting: { [scope: string]: number } = {};
    defaultSortSetting[this.recentlyUsedScopeId] = 0;
    defaultSortSetting[EntityScopeEnum.Project.toString()] = 1;
    defaultSortSetting[EntityScopeEnum.User.toString()] = 2;

    return await firstValueFrom(this.projectService.currentProject.pipe(
      switchMap(project => from(Promise.resolve(defaultSortSetting))
        .pipe(switchMap(defaultValue => from(this.settingsService.load(PersonalizationKeysEnum.PoolTypeSorting, SettingNamesEnum.PoolTypeSorting, project))
          .pipe(
            map(value => {
              return GetValue<{ [scope: string]: number }>(value, defaultValue);
            })
          )
        ))
      )));
  }

  private getPoolScopes(): void {
    const poolScopes: PoolScope[] = [];
    this.poolScopesLoaded = [];

    if (this.recentlyUsedPools.length > 0) {
      const recentlyUsedScope: PoolScope = {
        scope: this.recentlyUsedScopeId,
        text: 'MM.pool.leftPane.scopeRecentlyUsed',
        pools: this.recentlyUsedPools,
      };
      poolScopes.push(recentlyUsedScope);
    }

    const projectScope = this.createPoolScope(EntityScopeEnum.Project);
    const userScope = this.createPoolScope(EntityScopeEnum.User);

    poolScopes.push(projectScope);
    poolScopes.push(userScope);

    const recentlyUsedIds = new Set(this.recentlyUsedPools.map(p => p.Id));

    projectScope.pools.push(...this.poolsLoaded
      .filter(x => !!x.IsPublic && !recentlyUsedIds.has(x.Id))
      .sort((a, b) => (a.Name ?? '').localeCompare((b.Name ?? ''))));

    userScope.pools.push(...this.poolsLoaded
      .filter(x => !x.IsPublic && !recentlyUsedIds.has(x.Id))
      .sort((a, b) => (a.Name ?? '').localeCompare((b.Name ?? ''))));

    this.poolScopesLoaded = poolScopes
      .sort((a, b) => this.scopeSortSetting[a.scope.toString()] - this.scopeSortSetting[b.scope.toString()])
      .filter(x => (x.pools?.length ?? 0) > 0);
  }

  private createPoolScope(scope: EntityScopeEnum): PoolScope {
    const poolScope: PoolScope = {scope, pools: []};
    poolScope.pools = [];
    switch (scope) {
      case EntityScopeEnum.User:
        poolScope.text = 'MM.pool.leftPane.scopeUserText';
        poolScope.icon = 'person_outline';
        break;
      case EntityScopeEnum.Project:
        poolScope.text = 'MM.pool.leftPane.scopeProjectText';
        poolScope.icon = 'domain';
        break;
      default:
        break;
    }
    return poolScope;
  }

  getNodeExpandedState(node: TreeNode): boolean {
    if (this.nodeExpandedStates.hasOwnProperty(node.id)) {
      return this.nodeExpandedStates[node.id].isExpanded;
    }
    return true;
  }

  onPoolToggle(pool: Pool, isChecked: boolean): void {
    if (isChecked) {
      this.selectedPools.add(pool.Id);
    } else {
      this.selectedPools.delete(pool.Id);
    }
    this.selectedPoolsSubject.next(new Set(this.selectedPools));
  }

  isPoolSelected(pool: Pool): boolean {
    return this.selectedPools.has(pool.Id);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onDone(): void {
    if (this.showOfflineMessage() || this.selectedPools.size === 0) {
      return;
    }

    const result = {
      selectedPoolIds: Array.from(this.selectedPools),
      isAdd: this.dialogData.isAdd,
      fault: this.dialogData.fault,
      faults: this.dialogData.faults
    };

    this.dialogRef.close(result);
  }

  isPoolInRecentlyUsed(pool: Pool): boolean {
    return this.recentlyUsedPools.some(p => p.Id === pool.Id);
  }

  private showOfflineMessage(): boolean {
    if(this.connectivityService.isOfflineVal){
      this.dialogService.showOfflineNotificationDialog();
      return true;
    }
    return false;
  }
}
